import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { reportAPI, folderAPI } from '../services/api';
import ConfirmDialog from '../components/ConfirmDialog';
import AiOrganizeModal from '../components/AiOrganizeModal';
import { useTranslation } from 'react-i18next';
import './ReportsPage.css';

interface ReportListItem {
  id: number;
  name: string | null;
  status: string | null;
  created_at: string;
  updated_at?: string | null;
  ticks_used?: number | null;
  task_id?: string | null;
  folder_id?: number | null;
  summary_title: string | null;
  summary_text: string | null;
}

interface Folder {
  id: number;
  name: string;
  color: string | null;
  position: number;
  report_count: number;
  created_at: string;
}

const ReportsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('reports');
  const [reports, setReports] = useState<ReportListItem[]>([]);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [activeFolderId, setActiveFolderId] = useState<number | null>(null); // null = all
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Delete report
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState<boolean>(false);
  const [reportToDelete, setReportToDelete] = useState<number | null>(null);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);

  // Create folder
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');

  // Rename folder
  const [renamingFolderId, setRenamingFolderId] = useState<number | null>(null);
  const [renameFolderName, setRenameFolderName] = useState('');

  // Delete folder
  const [folderToDelete, setFolderToDelete] = useState<number | null>(null);
  const [isFolderDeleteOpen, setIsFolderDeleteOpen] = useState(false);

  // AI organize
  const [showAiOrganize, setShowAiOrganize] = useState(false);

  // Move report
  const [moveMenuReportId, setMoveMenuReportId] = useState<number | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
    }
  }, [navigate]);

  const fetchFolders = useCallback(async () => {
    try {
      const response = await folderAPI.getFolders();
      setFolders(response.data);
    } catch (err) {
      console.error('Error fetching folders:', err);
    }
  }, []);

  const fetchReports = useCallback(async (folderId?: number | null) => {
    try {
      setIsLoading(true);
      const filterValue = folderId === null ? undefined : folderId;
      const response = await reportAPI.getReportsList(filterValue);
      setReports(response.data);
    } catch (err) {
      console.error('Error fetching reports:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFolders();
    fetchReports();
  }, [fetchFolders, fetchReports]);

  const handleFolderClick = (folderId: number | null) => {
    setActiveFolderId(folderId);
    if (folderId === null) {
      fetchReports();
    } else {
      fetchReports(folderId);
    }
  };

  // --- Folder CRUD ---
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await folderAPI.createFolder({ name: newFolderName.trim() });
      setNewFolderName('');
      setShowCreateFolder(false);
      fetchFolders();
    } catch (err) {
      console.error('Error creating folder:', err);
    }
  };

  const handleRenameFolder = async (folderId: number) => {
    if (!renameFolderName.trim()) return;
    try {
      await folderAPI.updateFolder(folderId, { name: renameFolderName.trim() });
      setRenamingFolderId(null);
      setRenameFolderName('');
      fetchFolders();
    } catch (err) {
      console.error('Error renaming folder:', err);
    }
  };

  const openDeleteFolder = (folderId: number) => {
    setFolderToDelete(folderId);
    setIsFolderDeleteOpen(true);
  };

  const handleDeleteFolder = async () => {
    if (folderToDelete === null) return;
    try {
      await folderAPI.deleteFolder(folderToDelete);
      setIsFolderDeleteOpen(false);
      setFolderToDelete(null);
      if (activeFolderId === folderToDelete) {
        setActiveFolderId(null);
        fetchReports();
      }
      fetchFolders();
    } catch (err) {
      console.error('Error deleting folder:', err);
    }
  };

  // --- Report actions ---
  const openDeleteConfirmDialog = (reportId: number) => {
    setReportToDelete(reportId);
    setIsConfirmDialogOpen(true);
  };

  const closeDeleteConfirmDialog = () => {
    setIsConfirmDialogOpen(false);
    setReportToDelete(null);
  };

  const deleteReport = async () => {
    if (reportToDelete === null) return;
    try {
      await reportAPI.deleteReport(reportToDelete);
      setReports(reports.filter(report => report.id !== reportToDelete));
      closeDeleteConfirmDialog();
      fetchFolders(); // Update counts
    } catch (err) {
      console.error('Error deleting report:', err);
      setAlertMessage(t('error_delete'));
      closeDeleteConfirmDialog();
    }
  };

  const handleMoveReport = async (reportId: number, folderId: number | null) => {
    try {
      await folderAPI.moveReportToFolder(reportId, folderId);
      setMoveMenuReportId(null);
      fetchFolders();
      // Re-fetch reports with current filter
      if (activeFolderId === null) {
        fetchReports();
      } else {
        fetchReports(activeFolderId);
      }
    } catch (err) {
      console.error('Error moving report:', err);
    }
  };

  const handleAiApplied = () => {
    fetchFolders();
    fetchReports(activeFolderId);
  };

  return (
    <div className="main-container">
      <section className="reports-section visible">
        <div className="reports-header">
          <h2>{t('page_title')}</h2>
          <div className="reports-header-actions">
            <button
              className="header-action-btn ai-btn"
              onClick={() => setShowAiOrganize(true)}
              title={t('ai_organize')}
            >
              <i className="fas fa-magic"></i> AI
            </button>
            <button
              className="header-action-btn folder-btn"
              onClick={() => setShowCreateFolder(!showCreateFolder)}
              title={t('create_folder')}
            >
              <i className="fas fa-folder-plus"></i>
            </button>
            <button
              className="create-report-button"
              onClick={() => {
                if (activeFolderId && activeFolderId > 0) {
                  navigate(`/reports/create?folder=${activeFolderId}`);
                } else {
                  navigate('/reports/create');
                }
              }}
            >
              <i className="fas fa-plus"></i> {t('create_report')}
            </button>
          </div>
        </div>

        {showCreateFolder && (
          <div className="create-folder-inline">
            <input
              type="text"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder={t('folder_name_placeholder')}
              className="folder-name-input"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateFolder();
                if (e.key === 'Escape') { setShowCreateFolder(false); setNewFolderName(''); }
              }}
            />
            <button className="folder-save-btn" onClick={handleCreateFolder}>
              <i className="fas fa-check"></i>
            </button>
            <button className="folder-cancel-btn" onClick={() => { setShowCreateFolder(false); setNewFolderName(''); }}>
              <i className="fas fa-times"></i>
            </button>
          </div>
        )}

        <div className="reports-layout">
          {/* Folder sidebar */}
          <aside className="folders-sidebar">
            <div
              className={`folder-item ${activeFolderId === null ? 'active' : ''}`}
              onClick={() => handleFolderClick(null)}
            >
              <i className="fas fa-layer-group"></i>
              <span className="folder-name">{t('all_reports')}</span>
            </div>
            {folders.map(folder => (
              <div
                key={folder.id}
                className={`folder-item ${activeFolderId === folder.id ? 'active' : ''}`}
                onClick={() => handleFolderClick(folder.id)}
              >
                {renamingFolderId === folder.id ? (
                  <input
                    className="folder-rename-input"
                    value={renameFolderName}
                    onChange={(e) => setRenameFolderName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleRenameFolder(folder.id);
                      if (e.key === 'Escape') { setRenamingFolderId(null); setRenameFolderName(''); }
                    }}
                    onBlur={() => handleRenameFolder(folder.id)}
                    autoFocus
                    onClick={e => e.stopPropagation()}
                  />
                ) : (
                  <>
                    <span
                      className="folder-color-dot"
                      style={{ background: folder.color || '#3498db' }}
                    />
                    <span className="folder-name">{folder.name}</span>
                    <span className="folder-count">{folder.report_count}</span>
                    <div className="folder-actions" onClick={e => e.stopPropagation()}>
                      <button
                        className="folder-action-btn"
                        onClick={() => {
                          setRenamingFolderId(folder.id);
                          setRenameFolderName(folder.name);
                        }}
                        title={t('folder_rename')}
                      >
                        <i className="fas fa-pen"></i>
                      </button>
                      <button
                        className="folder-action-btn delete"
                        onClick={() => openDeleteFolder(folder.id)}
                        title={t('folder_delete')}
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
            <div
              className={`folder-item unfiled ${activeFolderId === 0 ? 'active' : ''}`}
              onClick={() => handleFolderClick(0)}
            >
              <i className="fas fa-inbox"></i>
              <span className="folder-name">{t('no_folder')}</span>
            </div>
          </aside>

          {/* Report cards */}
          <div className="reports-content">
            {isLoading ? (
              <div className="loading">{t('loading')}</div>
            ) : reports.length === 0 ? (
              <div className="no-reports">
                <p>{activeFolderId !== null ? t('empty_folder') : t('empty_all')}</p>
              </div>
            ) : (
              <div className="reports-list">
                {reports.map(report => (
                  <div key={report.id} className="report-card">
                    <div className="report-icon">
                      <i className="fas fa-file-alt"></i>
                    </div>
                    <div className="report-info">
                      <h3>{report.summary_title || t('report_title_fallback', { id: report.id })}</h3>
                      <div className="report-summary">
                        {report.summary_text || t('report_summary_hint')}
                      </div>
                      <div className="report-meta-row">
                        <span className="report-date">
                          {new Date(report.created_at).toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'ru-RU', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                        {report.folder_id && activeFolderId === null && (
                          <span className="report-folder-badge">
                            {folders.find(f => f.id === report.folder_id)?.name}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="report-actions">
                      <a
                        href={`/reports/${report.id}`}
                        className="button button-view"
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(`/reports/${report.id}`);
                        }}
                      >
                        {t('view_report')}
                      </a>
                      <div className="report-more-actions">
                        <button
                          className="button button-move"
                          onClick={() => setMoveMenuReportId(moveMenuReportId === report.id ? null : report.id)}
                          title={t('move_button')}
                        >
                          <i className="fas fa-folder-open"></i>
                        </button>
                        {moveMenuReportId === report.id && (
                          <div className="move-menu">
                            <div
                              className="move-menu-item"
                              onClick={() => handleMoveReport(report.id, null)}
                            >
                              <i className="fas fa-inbox"></i> {t('move_no_folder')}
                            </div>
                            {folders.map(f => (
                              <div
                                key={f.id}
                                className={`move-menu-item ${report.folder_id === f.id ? 'current' : ''}`}
                                onClick={() => handleMoveReport(report.id, f.id)}
                              >
                                <span className="folder-color-dot" style={{ background: f.color || '#3498db' }} />
                                {f.name}
                              </div>
                            ))}
                          </div>
                        )}
                        <button
                          className="button button-delete"
                          onClick={() => openDeleteConfirmDialog(report.id)}
                        >
                          <i className="fas fa-trash-alt"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <ConfirmDialog
        isOpen={isConfirmDialogOpen}
        title={t('delete_report_title')}
        message={t('delete_report_message')}
        onConfirm={deleteReport}
        onCancel={closeDeleteConfirmDialog}
      />

      <ConfirmDialog
        isOpen={isFolderDeleteOpen}
        title={t('delete_folder_title')}
        message={t('delete_folder_message')}
        onConfirm={handleDeleteFolder}
        onCancel={() => { setIsFolderDeleteOpen(false); setFolderToDelete(null); }}
      />

      <ConfirmDialog
        isOpen={alertMessage !== null}
        title="Error"
        message={alertMessage || ''}
        variant="alert"
        onConfirm={() => setAlertMessage(null)}
        onCancel={() => setAlertMessage(null)}
      />

      <AiOrganizeModal
        isOpen={showAiOrganize}
        onClose={() => setShowAiOrganize(false)}
        onApplied={handleAiApplied}
        reports={reports.map(r => ({
          id: r.id,
          title: r.summary_title || r.name || t('report_title_fallback', { id: r.id }),
        }))}
      />
    </div>
  );
};

export default ReportsPage;
